import { useState, useEffect, useContext } from "react";
import placeholder from "../inc/placeholder.jpg";
import { SiteContext } from "../contexts/SiteContext";
import { useAuth } from "../admin/contexts/AuthContext";
import fetchSite from "../utils/fetchSite";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import {
	Paper,
	Typography,
	makeStyles,
	Grid,
	Fade,
	useTheme,
} from "@material-ui/core";
import LayersIcon from "@material-ui/icons/Layers";
import Loading from "../components/Loading";
import Header from "../components/Header";
import AppBar from "../components/AppBar";
import Breadcrumbs from "../components/Breadcrumbs";
import Warning from "../components/Warning";
import Entries from "../components/Entries";
import People from "../components/People";
import Units from "../components/UnitList";
import NotFound from "../components/NotFound";
import AdminBar from "../components/AdminBar";
import NotAllowed from "../components/NotAllowed";
import Banner from "../components/Banner";

function Unit() {
	const { isAuth } = useAuth();
	const theme = useTheme();
	const [loading, setLoading] = useState(true);
	const [unit, setUnit] = useState();
	const { selectedSite, setSelectedSite } = useContext(SiteContext);
	const { siteSlug, unitSlug } = useParams();

	const useStyles = makeStyles((theme) => ({
		root: {
			flex: 1,
			paddingBottom: "4rem",
		},
		main: {
			margin: "0 auto",
			padding: "1rem",
			paddingTop: 0,
			maxWidth: process.env.REACT_APP_MAX_WIDTH,
		},
		paper: {
			padding: "1rem",
			marginBottom: "1rem",
		},
		featuredImage: {
			width: "100%",
		},
		button: {
			fontWeight: "bold",
			fontSize: ".7rem",
		},
		linkToMainUnit: {
			color:
				selectedSite && selectedSite.secondaryColor
					? selectedSite.secondaryColor
					: theme.palette.secondary.main,
		},
	}));

	const classes = useStyles();

	// This effect sets the selectedSite based on the :siteSlug (first url parameter)
	useEffect(() => {
		fetchSite(siteSlug, setSelectedSite);
	}, [siteSlug, setSelectedSite]);

	// This effect sets the unit and uses the selectedSite._id to make sure it's on the right site
	useEffect(() => {
		async function fetchUnit() {
			let res = await fetch(
				`${process.env.REACT_APP_API_URL}/units?site=${selectedSite._id}`,
				{ credentials: "include" }
			);
			res = await res.json();
			if (res.success) {
				res.data = res.data.filter((unit) => unit.slug === unitSlug);
				setUnit(res.data[0]);
				setLoading(false);
			} else {
				console.error(res.error);
			}
		}
		if (selectedSite) {
			fetchUnit();
		}
	}, [selectedSite, unitSlug]);

	if (loading || !selectedSite) {
		return <Loading />;
	}

	if (!loading && !unit) {
		return <NotFound />;
	}

	// If the resource/site isn't published and the user isn't logged in, don't show the page
	if (!isAuth && (!unit.published || !selectedSite.published)) {
		return <NotAllowed />;
	}

	return (
		<Fade in={typeof unit != "undefined"}>
			<div className={classes.root}>
				<AdminBar />
				<Header />
				<AppBar />
				<Banner />
				<Breadcrumbs
					links={[
						{
							name: "Units",
							url: `/${selectedSite.slug}/units`,
						},
						{
							name: unit.name,
							url: `/${selectedSite.slug}/units/${unit.slug}`,
						},
					]}
				/>
				<main className={classes.main}>
					<Grid container spacing={3}>
						{unit &&
							(unit.children.length > 0 ||
								unit.entries.length > 0 ||
								unit.people.length > 0 ||
								unit.featuredImage) && (
								<Grid item sm={12} md={4}>
									{unit && unit.featuredImage && (
										<Paper
											style={{ marginBottom: "1rem" }}
											className={classes.paper}
										>
											<img
												className={classes.featuredImage}
												src={
													unit.featuredImage
														? `${process.env.REACT_APP_FILE_URL}/${unit.featuredImage.fileName}`
														: //: "https://source.unsplash.com/random?collection=1995427&size=500x500"
														placeholder
												}
												alt={`${unit.name} featured`}
											/>
										</Paper>
									)}
									<Warning margin="0 0 1rem 0" />
								</Grid>
							)}
						<Grid
							item
							sm={12}
							md={
								unit.featuredImage ||
									unit.entries.length > 0 ||
									unit.people.length > 0 ||
									unit.children.length > 0
									? 8
									: 12
							}
						>
							<Paper className={classes.paper}>
								<Typography
									style={{
										color: selectedSite.secondaryColor
											? selectedSite.secondaryColor
											: theme.palette.primary.main,
									}}
									gutterBottom
									component="h2"
									variant="h6"
								>
									<LayersIcon />
									{!unit.parent ? (
										`Unit`
									) : (
										<>
											{"Sub-Unit of "}
											<Link
												className={classes.linkToMainUnit}
												to={`/${selectedSite.slug}/units/${unit.parent.slug}`}
											>
												{`${unit.parent.name}`}
											</Link>
										</>
									)}
								</Typography>
								<Typography
									style={{ marginTop: "1rem" }}
									gutterBottom
									component="h2"
									variant="h5"
								>
									{unit.name}
								</Typography>
								<div
									dangerouslySetInnerHTML={{ __html: unit.description }}
								></div>
								{unit.children.length > 0 && <Units parentUnit={unit} />}
							</Paper>
						</Grid>
					</Grid>
					{unit.entries.length > 0 && <Entries unit={unit} />}
					{unit.people.length > 0 && <People unit={unit} />}
				</main>
			</div>
		</Fade>
	);
}

export default Unit;
